import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { BrandMark } from "@/components/BrandMark";
import ThemePicker from "@/components/dashboard/ThemePicker";
import { ProBadge } from "@/components/ProBadge";
import { motion } from "framer-motion";
import { Heartbeat, Sparkle, Barbell, Moon, ForkKnife, ArrowRight, Brain, ShieldCheck, Pulse, TrendUp, Check, Watch, X } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { detectCurrency, PRICES, fmt } from "@/lib/billing";

const pillars = [
  { icon: Heartbeat, label: "Recovery", color: "var(--sage)" },
  { icon: Moon, label: "Sleep", color: "#8B7CF6" },
  { icon: Barbell, label: "Strength", color: "var(--brand)" },
  { icon: ForkKnife, label: "Nutrition", color: "#E8A54B" },
  { icon: Brain, label: "Mind", color: "#E2C275" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showLaunch, setShowLaunch] = useState(() => !localStorage.getItem("ikavio-launch-dismissed"));
  const cur = detectCurrency();
  const prices = PRICES[cur];

  const dismissLaunch = () => {
    localStorage.setItem("ikavio-launch-dismissed", "1");
    setShowLaunch(false);
  };

  return (
    <div className="min-h-screen bg-bg text-ink overflow-x-hidden">
      {showLaunch && (
        <div className="bg-ink text-bg" data-testid="launch-banner">
          <div className="max-w-6xl mx-auto px-6 py-2.5 flex items-center justify-center gap-3 text-sm">
            <Sparkle size={14} weight="fill" color="var(--brand)" className="shrink-0" />
            <span className="min-w-0">
              <span className="font-semibold">We just launched</span>
              <span className="hidden sm:inline"> — every new member gets a 7-day full Pro trial.</span>
            </span>
            <Link to="/register" data-testid="launch-banner-cta" className="shrink-0 font-semibold text-brand hover:underline">
              Claim your spot →
            </Link>
            <button data-testid="launch-banner-dismiss" onClick={dismissLaunch} aria-label="Dismiss announcement"
              className="shrink-0 opacity-60 hover:opacity-100 transition-opacity p-1">
              <X size={14} weight="bold" />
            </button>
          </div>
        </div>
      )}
      {/* Nav */}
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2" data-testid="brand-logo">
          <BrandMark size={32} />
          <span className="font-display text-xl font-semibold tracking-tight">Ikavio</span>
        </div>
        <div className="flex items-center gap-3">
          <ThemePicker />
          <Link to="/about" data-testid="nav-about-link" className="text-sm text-muted2 hover:text-ink transition-colors px-2 py-2 hidden sm:block">
            About
          </Link>
          <Link to="/glossary" data-testid="nav-glossary-link" className="text-sm text-muted2 hover:text-ink transition-colors px-2 py-2 hidden sm:block">
            Glossary
          </Link>
          <Link to="/login" data-testid="nav-login-link" className="text-sm text-muted2 hover:text-ink transition-colors px-4 py-2">
            Sign in
          </Link>
          <button
            data-testid="nav-getstarted-btn"
            onClick={() => navigate(user ? "/app" : "/register")}
            className="text-sm bg-ink text-bg hover:bg-brand transition-colors rounded-full px-5 py-2.5 font-medium"
          >
            Get started
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-10 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 label-eyebrow bg-surface border border-border rounded-full px-3 py-1.5">
              <Sparkle size={13} weight="fill" color="var(--brand)" /> AI-native performance OS
            </div>
            <h1 className="font-display font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
              Not a fitness app.<br />
              <span className="text-brand">An operating system</span> for your healthspan.
            </h1>
            <p className="text-muted2 text-base sm:text-lg mt-6 max-w-md leading-relaxed">
              Ikavio learns your body, sleep, stress and life — then decides your single most optimal day, every day.
              Bring the wearable you already own, or none at all.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              <button
                data-testid="hero-cta-btn"
                onClick={() => navigate(user ? "/app" : "/register")}
                className="group inline-flex items-center gap-2 bg-brand text-white rounded-full px-7 py-3.5 font-medium hover:bg-ink transition-colors"
              >
                Build my Digital Twin
                <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </button>
              <div className="flex items-center gap-2 text-sm text-muted2">
                <ShieldCheck size={16} weight="fill" color="var(--sage)" /> Private by default
              </div>
            </div>
          </motion.div>
        </div>

        {/* Hero visual: Healthspan ring */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="clay p-8 relative"
        >
          <div className="label-eyebrow">Healthspan Score</div>
          <div className="flex items-center justify-center py-6">
            <div className="relative w-56 h-56">
              <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                <circle cx="60" cy="60" r="52" fill="none" stroke="var(--border)" strokeWidth="10" />
                <circle cx="60" cy="60" r="52" fill="none" stroke="url(#g)" strokeWidth="10"
                  strokeLinecap="round" strokeDasharray={2 * Math.PI * 52}
                  strokeDashoffset={2 * Math.PI * 52 * (1 - 0.782)} />
                <defs>
                  <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="var(--brand)" />
                    <stop offset="100%" stopColor="var(--sage)" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-display font-light text-6xl tracking-tighter">782</span>
                <span className="label-eyebrow mt-1">of 1000</span>
              </div>
            </div>
          </div>
          <div className="flex justify-center mb-4">
            <div className="inline-flex items-center gap-2 bg-brand/10 text-brand rounded-full px-4 py-1.5 text-sm font-medium" data-testid="hero-days-added-chip">
              <TrendUp size={15} weight="bold" /> +2.4 healthy days added this month
            </div>
          </div>
          <div className="grid grid-cols-5 gap-2 mt-2">
            {pillars.map((p) => (
              <div key={p.label} className="flex flex-col items-center gap-1.5">
                <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: `${p.color}1a` }}>
                  <p.icon size={17} weight="fill" color={p.color} />
                </div>
                <span className="text-[10px] text-muted2">{p.label}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Differentiation: trackers vs Ikavio */}
      <section className="max-w-6xl mx-auto px-6 pb-20" data-testid="differentiation-section">
        <div className="clay p-8 md:p-12">
          <div className="label-eyebrow">Why Ikavio</div>
          <h2 className="font-display font-light tracking-tight text-2xl md:text-3xl mt-3 max-w-2xl leading-snug">
            A tracker tells you a score.{" "}
            <span className="text-brand">Ikavio tells you what to do about it.</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            <div className="rounded-2xl border border-border p-6" data-testid="compare-tracker-card">
              <div className="flex items-center gap-2 label-eyebrow">
                <Watch size={15} weight="fill" /> A wearable tracker
              </div>
              <ul className="mt-4 space-y-3 text-sm text-muted2">
                <li>Measures sleep, HRV and readiness</li>
                <li>Shows you charts and scores</li>
                <li>Leaves every decision to you</li>
              </ul>
            </div>
            <div className="rounded-2xl bg-brand/5 border border-brand/20 p-6" data-testid="compare-ikavio-card">
              <div className="flex items-center gap-2 label-eyebrow text-brand">
                <Pulse size={15} weight="fill" color="var(--brand)" /> Ikavio
              </div>
              <ul className="mt-4 space-y-3 text-sm">
                {[
                  "Plans your single most optimal day, every day",
                  "Adapts workouts, food and recovery to how you feel",
                  "Coaches you proactively — before problems happen",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <Check size={16} weight="bold" color="var(--brand)" className="mt-0.5 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-2.5" data-testid="wearable-compatibility-row">
            <span className="text-sm text-muted2 mr-1">Works with the wearable you already own —</span>
            {["Apple Health", "Oura", "Whoop", "Garmin", "Hume Band"].map((w) => (
              <span key={w} className="text-xs font-medium bg-secondary rounded-full px-3 py-1.5">{w}</span>
            ))}
            <span className="text-xs text-muted2">integrations rolling out</span>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="max-w-6xl mx-auto px-6 pb-24 grid md:grid-cols-3 gap-6">
        {[
          { icon: Brain, t: "Digital Human Twin", d: "A living model of your body that tracks biological age, days added to your healthspan, and predicts risks before they happen." },
          { icon: Pulse, t: "Daily Decision Engine", d: "Every day it decides: lift, walk, recover or rest — tuned to your sleep, stress, soreness and time." },
          { icon: ForkKnife, t: "Adaptive Nutrition", d: "No rigid meal plans. Contextual food guidance that flexes with travel, meetings and your goals." },
        ].map((c, i) => (
          <motion.div
            key={c.t}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            className="clay p-8"
          >
            <div className="w-11 h-11 rounded-2xl bg-brand/10 flex items-center justify-center">
              <c.icon size={22} weight="fill" color="var(--brand)" />
            </div>
            <h3 className="font-display text-xl font-medium mt-5">{c.t}</h3>
            <p className="text-muted2 text-sm mt-2 leading-relaxed">{c.d}</p>
          </motion.div>
        ))}
      </section>

      {/* Plans */}
      <section className="max-w-6xl mx-auto px-6 pb-24" data-testid="pricing-section">
        <div className="text-center max-w-xl mx-auto">
          <div className="label-eyebrow">Plans</div>
          <h2 className="font-display font-light tracking-tight text-2xl md:text-3xl mt-3 leading-snug">
            Start with a <span className="text-brand">7-day full Pro trial.</span> No card required.
          </h2>
          <p className="text-sm text-muted2 mt-3">Every new member gets everything unlocked for a week — then choose the plan that fits.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-10 max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.5 }} className="clay p-8" data-testid="plan-free-card">
            <h3 className="font-display text-xl font-medium">Free</h3>
            <p className="text-sm text-muted2 mt-1">The daily habit, forever free.</p>
            <div className="font-display text-3xl font-light mt-4">{fmt(0, cur)}<span className="text-sm text-muted2 font-sans"> / forever</span></div>
            <ul className="mt-6 space-y-2.5 text-sm">
              {["Daily check-in & optimal day plan", "Healthspan Score & streaks", "Breathing tool & pressure-off days", "Glossary & beginner mode"].map((f) => (
                <li key={f} className="flex items-start gap-2 text-muted2">
                  <Check size={16} weight="bold" color="var(--sage)" className="mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }} className="clay p-8 border-brand/40 relative" data-testid="plan-pro-card">
            <div className="flex items-center gap-2">
              <h3 className="font-display text-xl font-medium">Ikavio Pro</h3>
              <ProBadge testId="landing-pro-badge" />
            </div>
            <p className="text-sm text-muted2 mt-1">The full operating system.</p>
            <div className="mt-4">
              <div className="flex items-baseline gap-2" data-testid="pro-price-monthly">
                <span className="font-display text-3xl font-light text-brand">{fmt(prices.intro, cur)}</span>
                <span className="text-sm text-muted2">first month, then {fmt(prices.monthly, cur)}/mo</span>
              </div>
              <div className="text-xs text-muted2 mt-1.5" data-testid="pro-price-annual">
                or <span className="font-semibold text-ink">{fmt(prices.annual, cur)}/year</span> — 2 months free
              </div>
            </div>
            <ul className="mt-6 space-y-2.5 text-sm">
              {["Everything in Free", "Digital Twin — predictions, baselines & anomaly alerts", "Wearable integrations (Oura, Google Health, Apple Health)", "Ikavio Coach — unlimited AI coaching", "Weekly AI review & adaptive nutrition"].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check size={16} weight="bold" color="var(--brand)" className="mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <button
              data-testid="pricing-cta-btn"
              onClick={() => navigate(user ? "/app?tab=profile" : "/register")}
              className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-brand text-white rounded-full px-6 py-3 font-medium hover:bg-ink hover:text-bg transition-colors"
            >
              Start your 7-day Pro trial <ArrowRight size={17} weight="bold" />
            </button>
            <p className="text-[11px] text-muted2 text-center mt-3">Cancel anytime. Safety features always stay free.</p>
          </motion.div>
        </div>
      </section>

      <footer className="border-t border-border py-8 text-center text-sm text-muted2">
        <div className="flex items-center justify-center gap-5 flex-wrap">
          <span>Ikavio — Optimizing for healthy, capable years of life.</span>
          <Link to="/about" data-testid="footer-about-link" className="text-brand hover:underline font-medium">
            About
          </Link>
          <Link to="/glossary" data-testid="footer-glossary-link" className="text-brand hover:underline font-medium">
            Glossary
          </Link>
          <Link to="/privacy" data-testid="footer-privacy-link" className="text-brand hover:underline font-medium">
            Privacy
          </Link>
          <Link to="/terms" data-testid="footer-terms-link" className="text-brand hover:underline font-medium">
            Terms
          </Link>
        </div>
      </footer>
    </div>
  );
}
